import { MOCKUPS } from './mockups';
import type { Project } from '@/lib/content/projects';


export function ProjectCard({
  project,
  locale,
  variant,
  deployText,
  benefit,
  result,
}: {
  project: Project;
  locale: string;
  variant: string;
  deployText: string;
  benefit?: string;
  /** Optional accented result line (e.g. a key metric). Only passed on the homepage. */
  result?: string;
}) {
  const Mock = MOCKUPS[project.slug];
  const isHero    = variant === 'hero';
  const isHeroRev = variant === 'hero-rev';
  const isWide    = variant === 'wide';
  const isHeroAny = isHero || isHeroRev;

  const cardBase = [
    'group relative flex overflow-hidden rounded-2xl',
    'bg-white border border-[color:var(--case-studies-card-border)]',
    'shadow-[0_1px_2px_rgba(16,24,40,0.04),0_10px_30px_-18px_rgba(16,24,40,0.18)]',
    'transition-all duration-[280ms] ease-[cubic-bezier(0.2,0.7,0.3,1)]',
    'hover:-translate-y-1 hover:shadow-[0_22px_55px_-24px_rgba(16,24,40,0.28)]',
    'hover:border-[color:var(--case-studies-card-hover-border)]',
    'cursor-pointer no-underline text-inherit h-full',
  ].join(' ');

  const cardLayout = isHero
    ? 'flex-col lg:flex-row-reverse lg:min-h-[420px]'
    : isHeroRev
      ? 'flex-col lg:flex-row lg:min-h-[420px]'
      : 'flex-col';

  const mockupClass = isHeroAny
    ? 'relative bg-[var(--bg-mockup-dark)] overflow-hidden h-[200px] lg:h-auto lg:flex-[1.15]'
    : isWide
      ? 'relative bg-[var(--bg-mockup-dark)] overflow-hidden flex-none h-[200px] lg:h-[clamp(220px,17vw,280px)]'
      : 'relative bg-[var(--bg-mockup-dark)] overflow-hidden flex-none h-[200px] lg:h-[clamp(180px,14vw,220px)]';

  const bodyClass = isHeroAny
    ? 'relative flex flex-col flex-1 justify-center gap-[18px] p-8 lg:p-[clamp(32px,3vw,56px)]'
    : 'relative flex flex-col flex-1 gap-3.5 p-[clamp(20px,1.6vw,28px)]';

  const logoClass = isHeroAny
    ? 'self-auto flex-none h-[40px] max-w-[120px] flex items-center justify-end pointer-events-none'
    : 'self-auto flex-none h-[32px] max-w-[92px] flex items-center justify-end pointer-events-none';

  const tagsClass = 'flex items-center gap-2.5 flex-wrap';

  const titleClass = isHeroAny
    ? 'text-[clamp(2rem,1.6vw+1.4rem,2.9rem)] font-medium text-[color:var(--font-secondary)] leading-[1.08] tracking-[-0.01em] mt-0.5'
    : 'text-[length:var(--text-feature-title)] font-medium text-[color:var(--font-secondary)] leading-[1.08] tracking-[-0.01em] mt-0.5';

  const footerClass = isHeroAny
    ? 'mt-auto flex items-end justify-between gap-4 pt-2'
    : 'flex items-end justify-between gap-4 pt-4 border-t border-[color:var(--case-studies-card-border)] group-hover:border-[color:var(--case-studies-card-hover-border)]/80';

  const arrowClass = isHeroAny
    ? 'flex-none w-[46px] h-[46px] rounded-full flex items-center justify-center border border-[color:var(--case-studies-card-border)] text-[color:var(--font-secondary)] opacity-30 transition-all duration-[280ms] group-hover:opacity-100 group-hover:translate-x-1 group-hover:bg-[color:var(--font-secondary)] group-hover:text-white group-hover:border-[color:var(--font-secondary)]'
    : 'flex-none w-[38px] h-[38px] rounded-full flex items-center justify-center border border-[color:var(--case-studies-card-border)] text-[color:var(--font-secondary)] opacity-30 transition-all duration-[280ms] group-hover:opacity-100 group-hover:translate-x-1 group-hover:bg-[color:var(--font-secondary)] group-hover:text-white group-hover:border-[color:var(--font-secondary)]';

  return (
    <a
      href={`/${locale}/work/${project.slug}`}
      className={`${cardBase} ${cardLayout}`}
      aria-label={project.name}
    >
      <div className={mockupClass}>
        <div className="absolute inset-0">
          {Mock && <Mock />}
        </div>
      </div>

      <div className={bodyClass}>
        <div className="flex flex-row items-center justify-between gap-4">
          <h2 className={titleClass}>{project.name}</h2>
          {project.logo && (
            <span className={logoClass}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={project.logo}
                alt={project.client}
                className="h-full w-auto max-w-full object-contain object-right-center block opacity-[0.92]"
                loading="lazy"
              />
            </span>
          )}
        </div>
        <p className={`text-[length:var(--text-nav)] text-[color:var(--font-charcoal)]/80 leading-[1.55]`}>
          {benefit ?? project.tagline}
        </p>
        {result && (
          <p className="text-[length:var(--text-nav)] font-semibold text-[color:var(--font-navy)] leading-[1.5]">
            {result}
          </p>
        )}

        <div className={`${tagsClass} font-mono text-[length:var(--text-tag)] uppercase tracking-[0.1em] leading-none text-[color:var(--font-charcoal)]/70 pt-5 ${isHeroAny ? '' : 'mt-auto'}`}>
          <span>{project.industry}</span>
          <span className="w-1 h-1 rounded-full bg-[color:var(--font-charcoal)]/70 flex-none"></span>
          <span>{deployText}</span>
        </div>

        <div className={footerClass}>
          <div className="flex flex-col gap-[3px]">
            <span className="font-semibold text-[color:var(--font-secondary)] text-[0.98rem]">{project.client}</span>
            {project.clientCountry && (
              <span className="text-[0.84rem] text-[color:var(--font-charcoal)]/70">{project.clientCountry}</span>
            )}
          </div>
          <span className={arrowClass} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h9M8.5 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>
    </a>
  );
}
